import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { CheckCircle, AlertCircle, Building2, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import GlowBackground from '../components/GlowBackground';
import AuthModal from '../components/AuthModal';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, isAuthenticated } = useAuth();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/invites/${token}`)
      .then(r => setInvite(r.data))
      .catch(err => setError(err?.response?.data?.detail || 'Invite not found.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    if (!isAuthenticated) {
      setShowAuth(true);
      return;
    }
    setAccepting(true);
    try {
      await axios.post(`${API}/invites/${token}/accept`);
      toast.success(`Welcome to ${invite.organisation}!`);
      // Force a fresh /auth/me so user.is_org_member updates everywhere
      setTimeout(() => { window.location.href = '/profile'; }, 600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not accept the invite.');
    } finally {
      setAccepting(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FFF8F0' }}>
        <div className="w-10 h-10 rounded-full border-2 border-orange-primary/30 border-t-orange-primary animate-spin" />
      </div>
    );
  }

  if (error || !invite) {
    return (
      <div className="min-h-screen relative" style={{ backgroundColor: '#FFF8F0' }}>
        <GlowBackground />
        <Header />
        <main className="relative z-10 flex items-center justify-center px-6 py-20 text-center">
          <div>
            <AlertCircle size={36} className="text-charcoal/30 mx-auto mb-3" />
            <h2 className="font-serif text-2xl text-charcoal mb-2">This invite is no longer valid</h2>
            <p className="text-charcoal/60 mb-6">{error || 'It may have been revoked or expired.'}</p>
            <Link to="/" className="text-orange-primary hover:underline text-sm">Back to home</Link>
          </div>
        </main>
      </div>
    );
  }

  const blockingState = invite.expired
    ? { title: 'This invite has expired', message: 'Ask your team admin to send a fresh one.' }
    : invite.accepted
      ? { title: 'This invite has already been used', message: 'Try signing in instead.' }
      : null;

  if (blockingState) {
    return (
      <div className="min-h-screen relative" style={{ backgroundColor: '#FFF8F0' }}>
        <GlowBackground />
        <Header />
        <main className="relative z-10 flex items-center justify-center px-6 py-20 text-center">
          <div>
            <AlertCircle size={36} className="text-charcoal/30 mx-auto mb-3" />
            <h2 className="font-serif text-2xl text-charcoal mb-2">{blockingState.title}</h2>
            <p className="text-charcoal/60 mb-6">{blockingState.message}</p>
            <Link to="/" className="text-orange-primary hover:underline text-sm">Back to home</Link>
          </div>
        </main>
      </div>
    );
  }

  const wrongAccount = isAuthenticated && user && user.email?.toLowerCase() !== invite.email.toLowerCase();

  return (
    <div className="min-h-screen relative" style={{ backgroundColor: '#FFF8F0' }}>
      <GlowBackground />
      <Header />
      <Toaster position="top-center" richColors />

      <main className="relative z-10 max-w-xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl bg-white/60 border border-charcoal/5 p-6 sm:p-8 text-center"
        >
          <div className="w-14 h-14 rounded-2xl bg-orange-primary/10 flex items-center justify-center mx-auto mb-5">
            <Building2 size={26} className="text-orange-primary" />
          </div>
          <h1 className="font-serif text-3xl text-charcoal">
            You’re invited to join <span className="text-orange-primary">{invite.organisation}</span>
          </h1>
          <p className="text-charcoal/60 mt-2 text-sm">
            {invite.invited_by_name ? `${invite.invited_by_name} sent` : 'Someone sent'} this invite to{' '}
            <span className="font-medium text-charcoal">{invite.email}</span>
            {' '}as a {invite.role === 'admin' ? 'team admin' : 'team member'}.
          </p>

          {wrongAccount ? (
            <div className="mt-6 rounded-2xl bg-amber-50 border border-amber-200 p-4 text-left">
              <p className="text-sm text-amber-900">
                You’re signed in as <strong>{user.email}</strong> — but this invite is for <strong>{invite.email}</strong>.
                Sign out, then sign in with that email to accept.
              </p>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleAccept}
              disabled={accepting}
              data-testid="accept-invite-btn"
              className="mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-orange-primary text-white font-medium hover:bg-orange-primary/90 transition-colors disabled:opacity-50"
            >
              {accepting
                ? 'Joining…'
                : isAuthenticated
                  ? <>Join {invite.organisation} <ArrowRight size={16} /></>
                  : <>Sign in &amp; accept <ArrowRight size={16} /></>}
            </button>
          )}

          <p className="text-xs text-charcoal/40 mt-4">
            By joining you’ll be able to view your team’s directory and contribute to your team’s Wavelength sessions.
          </p>
        </motion.div>

        <p className="text-center text-xs text-charcoal/40 mt-4">
          Already know what this is? <Link to="/" className="text-orange-primary hover:underline">Take me home instead</Link>
        </p>
      </main>

      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
    </div>
  );
};

export default AcceptInvite;
